// Italian formatters and CRM constants

export const fmtEUR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
export const fmtEUR2 = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const fmtNum = (n, dec = 0) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec });
export const fmtPct = (n, dec = 1) => `${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec })}%`;

export const fmtDateIT = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};
export const fmtDateTimeIT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const todayISO = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
export const monthKeyOf = (d = new Date()) => d.toISOString().slice(0, 7);
export const addMonthsKey = (mk, delta) => {
  const [y, m] = mk.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
export const monthLabelIT = (mk) => {
  if (!mk) return "";
  const [y, m] = mk.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

// Stati cliente
export const STATI_CLIENTE_BASE = [
  "NON RISPONDE",
  "NON INT",
  "RECALL",
  "INTERESSATO",
  "TRATTATIVA",
  "ATTESA DOCUMENTI",
  "PROPORRE PP",
  "PP CREDITIS LIQUIDATO",
  "PP CREDITIS RIFIUTATO",
  "PP PREXTA RIFIUTATO",
  "PP LIQUIDATO",
  "DPR TERZI",
  "DPR TERZI SC",
  "NON REPERIBILE",
  "KO",
  "BLACK LIST",
];

const _readCustom = () => {
  try {
    const v = localStorage.getItem("ba_custom_states");
    if (!v) return [];
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string" && s.trim()) : [];
  } catch { return []; }
};

const _readHidden = () => {
  try {
    const v = localStorage.getItem("ba_hidden_states");
    if (!v) return [];
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch { return []; }
};

// STATI_CLIENTE: array dinamico = (base - nascosti) + custom utente.
const _build = () => {
  const hidden = new Set(_readHidden());
  const visibleBase = STATI_CLIENTE_BASE.filter((s) => !hidden.has(s));
  const custom = _readCustom().filter((s) => !STATI_CLIENTE_BASE.includes(s));
  return [...visibleBase, ...custom];
};

export let STATI_CLIENTE = _build();

export const refreshCustomStates = () => {
  STATI_CLIENTE = _build();
  try { window.dispatchEvent(new Event("ba_states_changed")); } catch {}
};

export const setCustomStates = (states) => {
  try { localStorage.setItem("ba_custom_states", JSON.stringify(states || [])); } catch {}
  refreshCustomStates();
};

export const setHiddenStates = (states) => {
  try { localStorage.setItem("ba_hidden_states", JSON.stringify(states || [])); } catch {}
  refreshCustomStates();
};

export const getCustomStates = () => _readCustom();
export const getHiddenStates = () => _readHidden();

// ============================================================================
// COLORI PERSONALIZZATI PER STATI
// ============================================================================
// Mappa { stateName: variantOrHex }, dove variant ∈ {success,warning,error,info,neutral,gold}
// oppure una stringa che inizia con "#" per un colore HEX custom.
const _readStateColors = () => {
  try {
    const v = localStorage.getItem("ba_state_colors");
    if (!v) return {};
    const o = JSON.parse(v);
    return o && typeof o === "object" ? o : {};
  } catch { return {}; }
};

export const setStateColors = (colors) => {
  try { localStorage.setItem("ba_state_colors", JSON.stringify(colors || {})); } catch {}
  try { window.dispatchEvent(new Event("ba_states_changed")); } catch {}
};
export const getStateColors = () => _readStateColors();

/** Restituisce {variant, hex} per uno stato.
 *  - se l'utente ha mappato lo stato a un variant predefinito → {variant}
 *  - se ha mappato a un hex custom → {variant: 'custom', hex}
 *  - altrimenti fallback al default deterministico statoClienteVariant
 */
export const resolveStatoColor = (s) => {
  if (!s) return { variant: "neutral" };
  const map = _readStateColors();
  const k = String(s).trim();
  const k2 = k.toUpperCase();
  const v = map[k] || map[k2];
  if (v) {
    if (typeof v === "string" && v.startsWith("#")) return { variant: "custom", hex: vividHex(v) };
    return { variant: v };
  }
  return { variant: statoClienteVariant(s) };
};

// Mappa variant → hex per il render di celle pienamente colorate (Monday-style)
// Palette ufficiale Monday.com: toni vivaci e saturi
export const VARIANT_HEX = {
  success: "#00C875",  // monday green
  warning: "#FDAB3D",  // monday orange
  error:   "#E2445C",  // monday red
  info:    "#579BFC",  // monday bright blue
  neutral: "#797E93",  // monday grey
  gold:    "#B7955B",  // brand gold
  purple:  "#A25DDC",  // monday purple
  pink:    "#FF158A",  // monday pink
  fire:    "#E10600",  // rosso fuoco (Black List)
};

// Rimappa i vecchi hex (palette Tailwind) sui toni vivaci Monday.
// Serve per "vividizzare" i colori già salvati nel DB senza migrazione.
const VIVID_HEX_MAP = {
  "#10b981": "#00C875", "#16a34a": "#00C875", "#22c55e": "#00C875", "#15803d": "#00C875",
  "#f59e0b": "#FDAB3D", "#eab308": "#FFCB00", "#d97706": "#FF642E",
  "#3b82f6": "#579BFC", "#0284c7": "#0086C0", "#0ea5e9": "#579BFC", "#2563eb": "#0086C0",
  "#ef4444": "#E2445C", "#dc2626": "#E2445C", "#b91c1c": "#BB3354",
  "#8b5cf6": "#A25DDC", "#7c3aed": "#A25DDC", "#6d28d9": "#784BD1", "#6366f1": "#784BD1",
  "#ec4899": "#FF158A", "#db2777": "#FF158A",
  "#14b8a6": "#4ECCC6", "#0d9488": "#4ECCC6",
  "#f97316": "#FF642E", "#ea580c": "#FF642E",
  "#64748b": "#797E93", "#94a3b8": "#C4C4C4", "#475569": "#797E93",
};
export const vividHex = (hex) => {
  if (!hex) return hex;
  return VIVID_HEX_MAP[String(hex).toLowerCase()] || hex;
};

// Testo bianco o scuro in base alla luminanza percepita dell'hex
export const isLightHex = (hex) => {
  if (!hex) return false;
  const v = String(hex).replace("#", "");
  if (v.length !== 6) return false;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 170;
};

/** Hex SEMPRE valido per uno stato. Usato per status-cell-full Monday-style. */
export const resolveStatoHex = (s) => {
  const r = resolveStatoColor(s);
  if (r.hex) return vividHex(r.hex);
  return VARIANT_HEX[r.variant] || VARIANT_HEX.neutral;
};

// Stati pratica mensile
export const STATI_PRATICA = [
  "IN SVOLGIMENTO",
  "ATTESA DELIBERA",
  "ATTESA DOCUMENTI",
  "ATTESA NOTIFICA",
  "ATTESA BENESTARE",
  "ATTESA CONTEGGI",
  "ATTESA DS",
  "LIQUIDATA",
  "RIFIUTATA",
  "RITIRATA",
];

export const PRIORITA = ["ALTA", "MEDIA", "BASSA"];

// Color mapping for client states (badge variants) — Monday-style premium
// Ogni stato ha un colore distintivo per leggibilità a colpo d'occhio
export const statoClienteVariant = (s) => {
  if (!s) return "neutral";
  const v = s.toUpperCase().trim();
  // BLACK LIST → rosso fuoco
  if (v === "BLACK LIST" || v === "BLACKLIST") return "fire";
  // LIQUIDATI / chiusi positivi → verde
  if (v.includes("LIQUIDATO") && !v.includes("RIFIUTATO")) return "success";
  // RIFIUTATI / KO → rosso
  if (v.includes("RIFIUTATO") || v === "KO") return "error";
  // INTERESSATO / PROPORRE → sky blue (lead caldo)
  if (v === "INTERESSATO" || v === "PROPORRE PP") return "info";
  // PROPOSTA / TRATTATIVA → viola (in trattativa attiva)
  if (v === "PROPOSTA" || v === "TRATTATIVA") return "purple";
  // DA CONTATTARE → viola acceso (Monday-style, stato dominante in tabella)
  if (v === "DA CONTATTARE") return "purple";
  // ATTESA → giallo/arancione
  if (v.includes("ATTESA")) return "warning";
  if (v === "RECALL") return "warning";
  // NEGATIVI ma non rifiuti → rosa (NON RISPONDE, NR)
  if (v === "NR" || v === "NON RISPONDE" || v === "NON REPERIBILE") return "pink";
  // NON INTERESSATO → grigio (definitivo ma non rifiutato)
  if (v === "NON INT" || v === "NON INTERESSATO") return "neutral";
  // DPR (vendor partner) → oro brand
  if (v.startsWith("DPR")) return "gold";
  return "neutral";
};

export const statoPraticaVariant = (s) => {
  if (!s) return "neutral";
  const v = s.toUpperCase();
  if (v === "LIQUIDATA") return "success";
  if (v === "RIFIUTATA" || v === "RITIRATA") return "error";
  if (v === "IN SVOLGIMENTO") return "info";
  if (v.startsWith("ATTESA")) return "warning";
  return "neutral";
};

export const crifVariant = (c) => {
  const v = (c || "").toUpperCase();
  if (v === "SI") return "error";
  if (v === "NO") return "success";
  return "neutral";
};

export const buildWAUrl = (phone, text) => {
  const ph = (phone || "").replace(/\D/g, "");
  const intl = ph.startsWith("39") || ph.length > 10 ? ph : `39${ph}`;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text || "")}`;
};

export const initials = (name = "", surname = "") => {
  const a = (name || "").trim().charAt(0).toUpperCase();
  const b = (surname || "").trim().charAt(0).toUpperCase();
  return (a + b) || "?";
};
